import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Aurora from '../components/Aurora';
import Logo from '../components/Logo';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: boolean;
    password?: boolean;
  }>({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  const validateFields = () => {
    const errors: typeof fieldErrors = {};
    let hasError = false;

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = true;
      hasError = true;
    }
    if (!password) {
      errors.password = true;
      hasError = true;
    }

    setFieldErrors(errors);
    return !hasError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!validateFields()) {
      setError('Please fill in all fields correctly');
      setIsLoading(false);
      return;
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errorType === 'USER_NOT_FOUND') {
          setError('No account found with this email address.');
          setFieldErrors(prev => ({ ...prev, email: true }));
        } else if (data.errorType === 'INVALID_PASSWORD') {
          setError('Incorrect password. Please try again.');
          setFieldErrors(prev => ({ ...prev, password: true }));
        } else {
          setError(data.message || 'Login failed. Please try again.');
        }
        return;
      }

      localStorage.setItem('token', data.token);
      window.dispatchEvent(new CustomEvent('authChange', { detail: { isAuthenticated: true } }));
      const from = (location.state as any)?.from?.pathname || '/projects';
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900 lg:flex-row" style={{ fontFamily: "'Geist', sans-serif" }}>
      {/* Aurora Background - Full coverage */}
      <div className="absolute inset-0 z-[1] bg-black text-white [&_canvas]:!w-full">
        <Aurora
          colorStops={["#1a28ea", "#8819d2", "#d71991"]}
          amplitude={0.8}
          blend={0.6}
          speed={0.5}
        />
      </div>

      {/* Left Panel - Hero */}
      <div
        className="relative z-[2] flex items-center justify-center p-8 text-center text-white lg:flex-1 lg:p-12"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.9s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div className="max-w-[480px]">
          <div className="mb-6 flex justify-center lg:mb-8">
            <Logo size="xl" className="cursor-default hover:translate-y-0 drop-shadow-[0_4px_20px_rgba(0,0,0,0.3)]" />
          </div>
          <h1
            className="mb-4 text-4xl font-bold leading-[1.1] tracking-[-1.5px] lg:text-6xl"
            style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 4px 30px rgba(0,0,0,0.3)' }}
          >
            Welcome Back
          </h1>
          <p className="mx-auto max-w-[360px] text-base leading-relaxed text-white/80 lg:text-lg">
            Sign in to continue creating amazing designs and managing your projects
          </p>
          {/* Decorative dots */}
          <div className="mt-8 hidden items-center justify-center gap-2 lg:flex">
            <div className="h-1.5 w-8 rounded-full bg-white/40" />
            <div className="h-1.5 w-1.5 rounded-full bg-white/25" />
            <div className="h-1.5 w-1.5 rounded-full bg-white/25" />
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="relative z-[2] flex flex-1 items-center justify-center p-6 lg:p-8">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-[420px] rounded-2xl border border-white/10 bg-white/95 p-8 shadow-[0_30px_80px_rgba(0,0,0,0.25)] backdrop-blur-xl dark:border-gray-700/50 dark:bg-gray-800/95 lg:rounded-3xl lg:p-10"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
            transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.2s',
          }}
        >
          <div className="mb-8 flex justify-center lg:hidden">
            <Logo className="cursor-default hover:translate-y-0 scale-125" />
          </div>

          <h2 className="mb-1 text-center text-3xl font-bold tracking-[-0.5px] text-gray-900 dark:text-white">
            Sign In
          </h2>
          <p className="mb-8 text-center text-sm text-gray-500 dark:text-gray-400">
            Enter your credentials to access your account
          </p>

          {/* Error Banner */}
          {error && (
            <div
              className="mb-6 flex items-center gap-3 rounded-xl bg-red-50 px-4 py-3 dark:bg-red-500/10"
              style={{ animation: 'authShake 0.4s ease-in-out' }}
            >
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
                <svg className="h-3 w-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Email Field */}
          <div
            className="mb-5"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(12px)',
              transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.3s',
            }}
          >
            <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors(prev => ({ ...prev, email: false }));
                setError('');
              }}
              aria-label="Email address"
              aria-invalid={fieldErrors.email}
              className={`w-full rounded-xl border-2 px-4 py-3.5 text-sm font-medium transition-all duration-200 placeholder:font-normal focus:outline-none focus:ring-0 ${
                fieldErrors.email
                  ? 'border-red-400 bg-red-50/50 text-red-700 placeholder:text-red-400/60 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.1)] dark:border-red-500/50 dark:bg-red-500/5 dark:text-red-400'
                  : 'border-gray-200 bg-gray-50/80 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_3px_rgba(99,102,241,0.12)] dark:border-gray-600 dark:bg-gray-700/50 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-indigo-400'
              }`}
              required
            />
          </div>

          {/* Password Field */}
          <div
            className="mb-2"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(12px)',
              transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.4s',
            }}
          >
            <label htmlFor="password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFieldErrors(prev => ({ ...prev, password: false }));
                  setError('');
                }}
                aria-label="Password"
                aria-invalid={fieldErrors.password}
                className={`w-full rounded-xl border-2 px-4 py-3.5 pr-12 text-sm font-medium transition-all duration-200 placeholder:font-normal focus:outline-none focus:ring-0 ${
                  fieldErrors.password
                    ? 'border-red-400 bg-red-50/50 text-red-700 placeholder:text-red-400/60 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.1)] dark:border-red-500/50 dark:bg-red-500/5 dark:text-red-400'
                    : 'border-gray-200 bg-gray-50/80 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_3px_rgba(99,102,241,0.12)] dark:border-gray-600 dark:bg-gray-700/50 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-indigo-400'
                }`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="mb-6 text-right">
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-indigo-500 transition-colors hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Forgot password?
            </Link>
          </div>

          {/* Submit Button */}
          <button
            id="login-button"
            type="submit"
            disabled={isLoading}
            className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl border-none px-6 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(99,102,241,0.35)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)',
            }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ transform: 'skewX(-20deg)' }} />
            {isLoading ? (
              <>
                <div className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white" style={{ animation: 'spin 0.6s linear infinite' }} />
                <span>Signing in...</span>
              </>
            ) : (
              <span>Sign In</span>
            )}
          </button>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">Don't have an account? </span>
            <Link
              to="/register"
              className="text-sm font-semibold text-indigo-500 transition-colors hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Create one
            </Link>
          </div>
        </form>
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes authShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-3px); }
          80% { transform: translateX(3px); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Login;
